// AdminNotificationsTab.jsx
import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Stack,
} from "@mui/material";
import { formatDistanceToNowStrict, format as formatDate } from "date-fns";

const normalize = (v) => String(v || "").trim().toUpperCase();

const getEmoji = (type) => {
  const t = normalize(type);
  if (t === "SQUARE_UNSYNC") return "🟦";
  if (t === "REQUEST_EDIT") return "📝";
  if (t === "REQUEST_OFF") return "🏖️";
  if (t === "DELETED_SALE") return "🗑️";
  if (t === "BREAK_30") return "⏸️";
  if (t === "LATE") return "⏰";
  if (t === "NO_CLOCK_IN") return "🚫";
  if (t === "MISSING_CLOCKOUT") return "🌗";
  if (t === "NO_MATERIAL") return "📦";
  if (t === "OPEN_SALES_CLOCKOUT") return "🧾";
  if (t === "CHECKLIST_OPENING_MISSING")    return "📋";
  if (t === "CHECKLIST_OPENING_INCOMPLETE") return "📋";
  if (t === "CHECKLIST_CLOSING_MISSING")    return "🌙";
  if (t === "CHECKLIST_CLOSING_INCOMPLETE") return "🌙";
  if (t === "CHECKLIST_REPORT_MISSING")     return "📄";
  if (t === "CHECKLIST_REPORT_INCOMPLETE")  return "📄";
  if (t === "LATE_CLOCKOUT")               return "🚪";
  return "🔔";
};

const humanizeType = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";
  return s
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const humanizeMaybe = (text) => {
  const s = String(text ?? "");
  if (!s) return "";
  const hasUnderscore = s.includes("_");
  const looksAllCaps = /^[A-Z0-9 _:-]+$/.test(s) && /[A-Z]/.test(s);
  if (!hasUnderscore && !looksAllCaps) return s;
  return s
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const typeMeta = (typeUpper) => {
  const t = normalize(typeUpper);

  
  const map = {
    BREAK_30: { label: "BREAK +30 MIN", color: "#f59e0b", tint: "rgba(245, 158, 11, 0.10)" },
    DELETED_SALE: { label: "DELETED SALE", color: "#ef4444", tint: "rgba(239, 68, 68, 0.10)" },
    LATE: { label: "LATE CLOCK-IN", color: "#fb7185", tint: "rgba(251, 113, 133, 0.10)" },
    NO_CLOCK_IN: { label: "NO CLOCK-IN", color: "#dc2626", tint: "rgba(220, 38, 38, 0.10)" },
    MISSING_CLOCKOUT: { label: "NO CLOCK-OUT", color: "#b91c1c", tint: "rgba(185, 28, 28, 0.10)" },
    NO_MATERIAL: { label: "MATERIAL", color: "#22c55e", tint: "rgba(34, 197, 94, 0.10)" },
    REQUEST_EDIT: { label: "EDIT REQUEST", color: "#6366f1", tint: "rgba(99, 102, 241, 0.10)" },
    REQUEST_OFF: { label: "OFF REQUEST", color: "#06b6d4", tint: "rgba(6, 182, 212, 0.10)" },
    OPEN_SALES_CLOCKOUT: { label: "OPEN SALES", color: "#f97316", tint: "rgba(249, 115, 22, 0.10)" },
    SQUARE_UNSYNC: { label: "UNLINKED", color: "#a855f7", tint: "rgba(168, 85, 247, 0.10)" },
    CHECKLIST_OPENING_MISSING:    { label: "OPENING MISSING",    color: "#f97316", tint: "rgba(249, 115, 22, 0.10)" },
    CHECKLIST_OPENING_INCOMPLETE: { label: "OPENING INCOMPLETE", color: "#f97316", tint: "rgba(249, 115, 22, 0.10)" },
    CHECKLIST_CLOSING_MISSING:    { label: "CLOSING MISSING",    color: "#8b5cf6", tint: "rgba(139, 92, 246, 0.10)" },
    CHECKLIST_CLOSING_INCOMPLETE: { label: "CLOSING INCOMPLETE", color: "#8b5cf6", tint: "rgba(139, 92, 246, 0.10)" },
    CHECKLIST_REPORT_MISSING:     { label: "REPORT MISSING",     color: "#0ea5e9", tint: "rgba(14, 165, 233, 0.10)" },
    CHECKLIST_REPORT_INCOMPLETE:  { label: "REPORT INCOMPLETE",  color: "#0ea5e9", tint: "rgba(14, 165, 233, 0.10)" },
    LATE_CLOCKOUT:                { label: "LATE CLOCK-OUT",     color: "#e11d48", tint: "rgba(225, 29, 72, 0.10)" },
  };

  return (
    map[t] || {
      label: humanizeType(t || "Notification"),
      color: "rgba(0,0,0,0.35)",
      tint: "rgba(0,0,0,0.03)",
    }
  );
};

const baseCardSx = {
  borderRadius: 3,
  overflow: "hidden",
  boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
  border: "1px solid rgba(0,0,0,0.08)",
  borderLeft: "6px solid transparent",
};

const safeTimeAgo = (ts) => {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    // ✅ tira "about" se por algum motivo aparecer
    return formatDistanceToNowStrict(d, { addSuffix: true }).replace(/^about\s+/i, "");
  } catch {
    return "";
  }
};

const getEventTs = (n) => {
  return (
    n?.payload?.event_ts ||
    n?.payload?.square_created_at ||
    n?.payload?.deleted_at ||
    n?.payload?.created_at ||
    n?.updated_at ||
    n?.created_at ||
    null
  );
};

const safeFormatDate = (ts) => {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return formatDate(d, "MMM dd, yyyy");
  } catch {
    return "";
  }
};

const sortByNewestFirst = (arr = []) => {
  return [...arr].sort((a, b) => {
    const ta = new Date(getEventTs(a) || 0).getTime();
    const tb = new Date(getEventTs(b) || 0).getTime();
    return tb - ta; // 🔥 mais novo primeiro
  });
};

const SwipeResolveCard = ({
  notifKey,
  n,
  children,
  onResolve,
  onOpen,
  accentColor = "#22c55e",
  disabledResolve = false,
  disabledOpen = false,
  openable = false,
}) => {
  const THRESHOLD = 120;

  const [dragX, setDragX] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isResolving, setIsResolving] = React.useState(false);

  const startXRef = React.useRef(0);
  const startYRef = React.useRef(0);
  const didTriggerResolveRef = React.useRef(false);
  const didMoveRef = React.useRef(false);

  // ✅ novo: quando identifica swipe horizontal, “captura” o gesto e trava o scroll
  const gestureCapturedRef = React.useRef(false);

  const rootRef = React.useRef(null);

  const resetGesture = () => {
    gestureCapturedRef.current = false;
    didMoveRef.current = false;
  };

  const handleStart = (clientX, clientY = 0) => {
    if (disabledResolve || isResolving) return;
    didTriggerResolveRef.current = false;
    resetGesture();
    startXRef.current = clientX;
    startYRef.current = clientY;
    setIsDragging(true);
  };

  const handleMoveCore = (dx, dy) => {
    if (!isDragging || disabledResolve || isResolving) return;

    // já “decidiu” que foi movimento
    if (!didMoveRef.current) didMoveRef.current = true;

    const clamped = Math.max(0, Math.min(420, dx));
    setDragX(clamped);
  };

  const shouldCaptureHorizontal = (dx, dy) => {
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    // pequeno demais: ignora
    if (adx < 10 && ady < 10) return false;

    // horizontal ganha
    return adx > ady * 1.15;
  };

  const finish = () => {
    setIsDragging(false);

    if (didTriggerResolveRef.current) return;

    if (dragX >= THRESHOLD && notifKey && !disabledResolve && !isResolving) {
      didTriggerResolveRef.current = true;
      setIsResolving(true);

      // anima “indo embora”
      setDragX(420);

      Promise.resolve(onResolve?.(notifKey, n)).finally(() => {
        setTimeout(() => {
          setIsResolving(false);
          setDragX(0);
          didTriggerResolveRef.current = false;
          resetGesture();
        }, 650);
      });

      return;
    }

    setDragX(0);
    resetGesture();
  };

  // ✅ adiciona listeners nativos (passive:false) pra garantir preventDefault no iOS/Chrome
  React.useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const onTouchMoveNative = (e) => {
      if (disabledResolve || isResolving) return;
      if (!isDragging) return;

      const t = e.touches?.[0];
      if (!t) return;

      const dx = t.clientX - startXRef.current;
      const dy = t.clientY - startYRef.current;

      // decide capturar
      if (!gestureCapturedRef.current) {
        if (shouldCaptureHorizontal(dx, dy)) {
          gestureCapturedRef.current = true;
        } else {
          // não capturou: deixa o scroll vertical acontecer
          return;
        }
      }

      // capturado: trava scroll/gesto do container
      e.preventDefault();
      e.stopPropagation();

      handleMoveCore(dx, dy);
    };

    el.addEventListener("touchmove", onTouchMoveNative, { passive: false });

    return () => {
      el.removeEventListener("touchmove", onTouchMoveNative);
    };
  }, [disabledResolve, isResolving, isDragging]);

  const onCardClick = () => {
    if (isResolving) return;
    if (isDragging) return;
    if (dragX > 8) return;
    if (didMoveRef.current) return;

    if (!disabledOpen && openable) {
      onOpen?.(n);
    }
  };

  const showResolveBg = dragX > 10;
  const revealPx = Math.max(0, Math.min(dragX, 999));

  return (
    <Box sx={{ position: "relative" }}>
      {/* Fundo (aparece ao arrastar) - CLARO e SÓ onde foi revelado */}
     

      <Box
        ref={rootRef}
        // mouse
        onMouseDown={(e) => handleStart(e.clientX, e.clientY)}
        onMouseMove={(e) => {
          if (!isDragging || disabledResolve || isResolving) return;
          const dx = e.clientX - startXRef.current;
          const dy = e.clientY - startYRef.current;

          // no desktop não precisa "capturar", mas mantém comportamento
          handleMoveCore(dx, dy);
        }}
        onMouseUp={finish}
        onMouseLeave={finish}
        // touch start/end (move é via listener nativo)
        onTouchStart={(e) => {
          const t = e.touches?.[0];
          if (!t) return;
          handleStart(t.clientX, t.clientY);
        }}
        onTouchEnd={finish}
        onClick={onCardClick}
        sx={{
          transform: `translateX(${dragX}px)`,
          cursor:
            openable && !disabledOpen
              ? "pointer"
              : disabledResolve
              ? "default"
              : isDragging
              ? "grabbing"
              : "grab",
          opacity: isResolving ? 0 : 1,
          transitionProperty: isDragging ? "none" : "transform, opacity",
          transitionDuration: isDragging ? "0ms" : "200ms",
          transitionTimingFunction: "ease",
          WebkitTapHighlightColor: "transparent",
          userSelect: "none",

          // ✅ chave: quando estiver capturando swipe, trava gestos do browser
          touchAction: isDragging && gestureCapturedRef.current ? "none" : "pan-y",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const AdminNotificationsTab = ({
  items,
  loading,
  onResolve,
  onOpenSquare,
  onOpenEditRequests,
  onOpenOffRequests,
}) => {
  // ✅ mantém scroll e lista fluida (sem mudar UI)
  const rootRef = React.useRef(null);
  const [localItems, setLocalItems] = React.useState(items || []);
  const pendingRestoreRef = React.useRef(null);

  const findScrollParent = React.useCallback((node) => {
    if (!node) return window;
    let el = node.parentElement;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      const canScroll =
        (overflowY === "auto" || overflowY === "scroll") &&
        el.scrollHeight > el.clientHeight;
      if (canScroll) return el;
      el = el.parentElement;
    }
    return window;
  }, []);

  const restoreScroll = React.useCallback((info) => {
    if (!info) return;
    try {
      if (info.isWindow) window.scrollTo(0, info.top);
      else if (info.el) info.el.scrollTop = info.top;
    } catch {}
  }, []);

  // ✅ quando o pai atualizar (refetch), só sincroniza lista e restaura scroll se necessário
React.useEffect(() => {
  setLocalItems(sortByNewestFirst(items || []));

  const pending = pendingRestoreRef.current;
  if (pending) {
    pendingRestoreRef.current = null;
    requestAnimationFrame(() => restoreScroll(pending));
  }
}, [items, restoreScroll]);



  // ✅ resolve fluido: remove do UI na hora, chama API, mantém posição
  const handleResolve = React.useCallback(
    async (notification_key, notifObj) => {
      if (!notification_key) return;

      const scrollEl = findScrollParent(rootRef.current);
      const isWindow = scrollEl === window;
      const top = isWindow ? window.scrollY : scrollEl.scrollTop;
      const restoreInfo = { el: isWindow ? null : scrollEl, top, isWindow };

      // garante que depois do refetch do pai a posição continua
      pendingRestoreRef.current = restoreInfo;

      // ✅ optimistic remove
      setLocalItems((prev) =>
        (prev || []).filter((x) => x.notification_key !== notification_key)
      );

      requestAnimationFrame(() => restoreScroll(restoreInfo));

      try {
        await Promise.resolve(onResolve?.(notification_key));
      } catch (e) {
        // rollback se falhar
        setLocalItems((prev) => {
          const exists = (prev || []).some((x) => x.notification_key === notification_key);
          if (exists) return prev || [];
          return sortByNewestFirst([notifObj, ...(prev || [])]);
        });
      } finally {
        requestAnimationFrame(() => restoreScroll(restoreInfo));
      }
    },
    [findScrollParent, onResolve, restoreScroll]
  );

  // ✅ AQUI é o pulo do gato:
  // não desmonta a lista inteira quando loading=true.
  // Só mostra spinner fullscreen se NÃO tiver nada pra mostrar ainda.
  if (loading && !(localItems && localItems.length)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box ref={rootRef} sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>
      <Stack spacing={1.25}>
        {(localItems || []).map((n) => {
          const notifKey = n.notification_key;
          const typeUpper = normalize(n.notification_type);
          const meta = typeMeta(typeUpper);
          const emoji = getEmoji(typeUpper);

          const ts = getEventTs(n);
          const timeAgo = safeTimeAgo(ts);
          const dateStr = safeFormatDate(ts);

          // 🔥 mantém o mesmo comportamento do seu código:
          // openable = !!onOpen (o "onOpen" vem de fora via payload, igual no teu original)
          const openable = true;

          // mensagem do jeito que você já tinha
          const message = humanizeMaybe(n.message);

          // payload pra decidir navegação (igual tua lógica atual de fora)
          const payload = n.payload;

          const onOpen = (p) => {
            // ⚠️ aqui você pode manter EXATAMENTE sua regra anterior
            // Eu mantive um default simples baseado em notification_type:
            const t = normalize(n.notification_type);

            if (t === "SQUARE_UNSYNC") return onOpenSquare?.(p);
            if (t === "REQUEST_EDIT") return onOpenEditRequests?.(p);
            if (t === "REQUEST_OFF") return onOpenOffRequests?.(p);

            // fallback: se não tiver rota, não faz nada
            return null;
          };

          return (
            <SwipeResolveCard
              key={notifKey || `${typeUpper}-${String(ts || "")}`}
              notifKey={notifKey}
              n={n}
              onResolve={(k, obj) => handleResolve(k, obj)}
              onOpen={() => onOpen?.(payload)}
              openable={openable}
              accentColor="#22c55e"
              disabledResolve={!notifKey}
              disabledOpen={!openable}
            >
              <Card
                sx={{
                  ...baseCardSx,
                  borderLeftColor: meta.color,
                  backgroundColor: meta.tint,
                  ...(openable
                    ? {
                        "&:hover": {
                          boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
                          transform: "translateY(-1px)",
                          transition: "box-shadow 160ms ease, transform 160ms ease",
                        },
                      }
                    : {}),
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.25, minWidth: 0, alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "999px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 22,
                        backgroundColor: "rgba(255,255,255,0.75)",
                        border: `1px solid rgba(0,0,0,0.10)`,
                        boxShadow: "0 6px 14px rgba(0,0,0,0.05)",
                        flexShrink: 0,
                      }}
                      aria-label={`notification-icon-${typeUpper}`}
                    >
                      {emoji}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        <Chip
                          size="small"
                          label={meta.label}
                          sx={{
                            fontWeight: 950,
                            borderRadius: "999px",
                            backgroundColor: "rgba(255,255,255,0.72)",
                            border: `1px solid ${meta.color}55`,
                            color: "rgba(0,0,0,0.82)",
                          }}
                        />

                        {timeAgo ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={timeAgo}
                            sx={{
                              fontWeight: 850,
                              borderRadius: "999px",
                              borderColor: `${meta.color}55`,
                              backgroundColor: "rgba(255,255,255,0.55)",
                            }}
                          />
                        ) : null}
                      </Box>

                      {message ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.35, fontWeight: 700, wordBreak: "break-word" }}
                        >
                          {message}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </SwipeResolveCard>
          );
        })}
      </Stack>
    </Box>
  );
};